import React, { useState } from 'react'
import { useAdminAnalytics } from '../hooks/useAdminAnalytics.js'
import { KpiCard } from '../components/analytics/KpiCard.jsx'
import { CadenceHeatmap } from '../components/analytics/CadenceHeatmap.jsx'
import { PrepQualityDonut } from '../components/analytics/PrepQualityDonut.jsx'
import { AdoptionFunnel } from '../components/analytics/AdoptionFunnel.jsx'
import { PrepCardUsage } from '../components/analytics/PrepCardUsage.jsx'
import { GoalsHealthTable } from '../components/analytics/GoalsHealthTable.jsx'

const PERIODS = [
  { value: '4w', label: '4w' },
  { value: '12w', label: '12w' },
  { value: '6m', label: '6m' },
]

export function AnalyticsPage({ onNavigate }) {
  const [period, setPeriod] = useState('12w')
  const { data, loading, error } = useAdminAnalytics(period)

  const kpis = data?.kpis
  const sessionsHeld = kpis?.sessionsHeld ?? 0
  const sessionsDelta = kpis ? kpis.sessionsHeld - kpis.prevSessionsHeld : 0
  const mutualDelta = kpis ? kpis.mutualPrepRate - kpis.prevMutualPrepRate : 0
  const goalsDelta = kpis ? kpis.goalsOnTrackRate - kpis.prevGoalsOnTrackRate : 0

  const prepCardUsage = data?.prepCardUsage || []
  const topCards = prepCardUsage.slice(0, 8)
  const bottomCards = prepCardUsage.length > 8
    ? prepCardUsage.slice(-8).reverse()
    : [...prepCardUsage].reverse()

  function handleGoalRow(row) {
    if (onNavigate) onNavigate({ type: 'user', userId: row.reportId })
  }

  return (
    <div>
      <div style={styles.header}>
        <h2 style={styles.title}>Analytics</h2>
        <div style={styles.periods}>
          {PERIODS.map((p) => (
            <button
              key={p.value}
              style={{ ...styles.periodBtn, ...(period === p.value ? styles.periodActive : {}) }}
              onClick={() => setPeriod(p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div style={styles.error}>Failed to load analytics.</div>}

      {!data && loading && <div style={styles.loading}>Loading…</div>}

      {data && (
        <div style={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
          {sessionsHeld === 0 && kpis?.activePairs === 0 ? (
            <div style={styles.emptyAll}>
              No sessions completed in this period. Data will appear once your team starts running 1:1s.
            </div>
          ) : (
            <>
              <div style={styles.kpiGrid}>
                <KpiCard label="Active pairs" value={kpis.activePairs} />
                <KpiCard
                  label="Sessions held"
                  value={kpis.sessionsHeld}
                  delta={sessionsDelta}
                  deltaLabel="vs prev period"
                  tone={sessionsDelta >= 0 ? 'up' : 'down'}
                />
                <KpiCard
                  label="Mutual prep rate"
                  value={`${kpis.mutualPrepRate}%`}
                  delta={mutualDelta}
                  deltaLabel="vs prev"
                  tone={mutualDelta >= 0 ? 'up' : 'down'}
                />
                <KpiCard
                  label="Goals on track"
                  value={`${kpis.goalsOnTrackRate}%`}
                  delta={goalsDelta}
                  deltaLabel="vs prev"
                  tone={goalsDelta >= 0 ? 'up' : 'down'}
                />
                <KpiCard
                  label="Inactive pairs"
                  value={kpis.inactivePairs}
                  delta={kpis.inactivePairs > 0 ? 'no session in 3+ weeks' : 'all active'}
                  tone={kpis.inactivePairs > 0 ? 'down' : 'up'}
                />
              </div>

              <div style={{ ...styles.panel, marginTop: 16 }}>
                <div style={styles.panelTitle}>Session cadence</div>
                <CadenceHeatmap rows={data.cadenceByManager} />
              </div>

              <div style={styles.cardGrid}>
                <div style={styles.panel}>
                  <div style={styles.panelTitle}>Prep quality</div>
                  <PrepQualityDonut breakdown={data.prepBreakdown} />
                </div>
                <div style={styles.panel}>
                  <div style={styles.panelTitle}>Adoption funnel</div>
                  <AdoptionFunnel funnel={data.adoptionFunnel} />
                </div>
                <div style={styles.panel}>
                  <div style={styles.panelTitle}>Most-used prep cards</div>
                  <PrepCardUsage cards={topCards} />
                </div>
                <div style={styles.panel}>
                  <div style={styles.panelTitle}>Least-used prep cards</div>
                  <PrepCardUsage cards={bottomCards} />
                </div>
              </div>

              <div style={{ ...styles.panel, marginTop: 16 }}>
                <div style={styles.panelTitle}>Goals health</div>
                <GoalsHealthTable rows={data.goalsPerPair} onRowClick={handleGoalRow} />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  title: { fontSize: 20, fontWeight: 600 },
  periods: { display: 'flex', gap: 4 },
  periodBtn: { padding: '5px 12px', borderRadius: 20, border: '1px solid var(--accent-brand)', background: 'none', fontSize: 12, cursor: 'pointer', color: 'var(--accent-brand)' },
  periodActive: { background: 'var(--accent-brand)', color: '#fff', border: '1px solid var(--accent-brand)' },
  loading: { fontSize: 13, color: 'var(--text-secondary)', padding: 32 },
  error: { fontSize: 13, color: '#8A2A22', background: '#F8D6D3', padding: 10, borderRadius: 6, marginBottom: 16 },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 },
  cardGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16, gridAutoRows: '1fr' },
  panel: { background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 20, border: '1px solid var(--border)' },
  panelTitle: { fontSize: 13, fontWeight: 600, marginBottom: 14 },
  emptyAll: { textAlign: 'center', padding: 60, fontSize: 14, color: 'var(--text-secondary)' },
}
